/* global localStorage console window */
import * as React from "react";
import { useEffect } from "react";
import PropTypes from "prop-types";
import { makeStyles, Spinner } from "@fluentui/react-components";
import { useDispatch, useSelector, useStore } from "react-redux";
import { select } from "../state/selectionSlice";
import { applyStylesheet } from "../state/cellStylesSlice";
import { getTemplateDefs } from "../helpers/grid";
import Cell from "./cells/Cell";
import SeparatorCell from "./SeparatorCell";
import Selection from "./adorners/Selection";
import Borders from "./Borders";
import GriddMenu from "./contextmenus/GriddMenu";
import StackMenu from "./contextmenus/StackMenu";
import ColumnHeaders from "./adorners/ColumnHeaders";
import RowHeaders from "./adorners/RowHeaders";
import { updateCell } from "../state/structureSlice";
import { safeGetCurrentSlideId, safeStoreGriddOnShape } from "../ppt/safePPT";
import { saveToExistingHandle } from "../helpers/fileSystem";
import { debounce } from "../helpers/debounce";
import exists from "../helpers/exists";

// Trailing-debounce window for persistence. A burst of dispatches (e.g. autofit
// firing many resize actions, or applying a stylesheet) collapses into a single
// serialize + write after activity settles.
const PERSIST_DEBOUNCE_MS = 400;

const useClasses = makeStyles({
  grid: {
    display: "grid",
    width: "100%",
  },
});

const Gridd = ({ gridRef, presId, slideId }) => {
  const classes = useClasses();
  // Only render-needed slices are subscribed. borders/cellStyles/spacerStyles/
  // merged/localStorageKey are used solely for persistence and are read from
  // store.getState() in the debounced save + a store.subscribe trigger below, so
  // style edits no longer re-render Gridd. (moatColor keeps a narrow selector.)
  const cols = useSelector((state) => state.gridd.present.structure.columns);
  const rows = useSelector((state) => state.gridd.present.structure.rows);
  const cells = useSelector((state) => state.gridd.present.structure.cells);
  const currentStyleSheet = useSelector(
    (state) => state.styleSheet.sheets[state.styleSheet.selectedSheet] ?? state.styleSheet.sheets["default"]
  );
  const moatColor = useSelector((state) => state.gridd.present.cellStyles.global?.moatColor);
  const readOnly = useSelector((state) => state.app.readOnlyMode);
  const dispatch = useDispatch();
  const store = useStore();

  // A single stable, debounced persist function. It reads fresh state from
  // store.getState() at flush time rather than closing over the subscribed
  // slices, so rapid bursts of dispatches collapse into one write.
  const persistGridd = React.useMemo(
    () =>
      debounce(async () => {
        const state = store.getState();
        const { columns: cols, rows, cells, merged } = state.gridd.present.structure;
        if ((cols?.length ?? 0) === 0 || (rows?.length ?? 0) === 0 || (cells?.length ?? 0) === 0) {
          return;
        }
        const borders = state.gridd.present.borders;
        const cellStyles = state.gridd.present.cellStyles;
        const spacerStyles = state.gridd.present.spacerStyles;
        const currentSlideId = state.app.currentSlideId;
        const localStorageKey = state.app.localStorageKey;

        let pid = presId;
        let sid = slideId;
        if (!pid || !sid || pid === "PRES" || sid === "SLIDE") {
          const currentSlide = await safeGetCurrentSlideId();
          pid = currentSlide.presId;
          sid = currentSlide.slideId;
        }

        const reducedCells = [].concat(...cells);
        const cleanedCellStyles = {
          global: cellStyles.global,
          assets: cellStyles.assets,
          shapeGroups: cellStyles.shapeGroups,
        };
        reducedCells.forEach((cell) => (cleanedCellStyles[cell.id] = cellStyles[cell.id]));
        const cleanedBorders = borders.filter((border) =>
          border.isVertical ? border.from < rows.length : border.from < cols.length
        );
        const gridd = {
          cols,
          rows,
          cells,
          borders: cleanedBorders,
          cellStyles: cleanedCellStyles,
          spacerStyles,
          merged,
        };
        const stringified = JSON.stringify(gridd);
        const success = await safeStoreGriddOnShape(currentSlideId ?? sid, stringified);
        if (!success) {
          localStorage.setItem(localStorageKey ?? pid + (currentSlideId ?? sid) + "_GRIDD", stringified);
        }
        await saveToExistingHandle(store.getState());
      }, PERSIST_DEBOUNCE_MS),
    [store, presId, slideId]
  );

  // Trigger persistence on any grid-relevant change without subscribing the
  // component to those slices (which would re-render Gridd on every style edit).
  // A single store subscription compares slice references and fires the debounced
  // save; `structure` covers cols/rows/cells/merged. The debounce coalesces bursts.
  useEffect(() => {
    const getRelevant = () => {
      const s = store.getState();
      return {
        structure: s.gridd.present.structure,
        borders: s.gridd.present.borders,
        cellStyles: s.gridd.present.cellStyles,
        spacerStyles: s.gridd.present.spacerStyles,
        localStorageKey: s.app.localStorageKey,
      };
    };
    let prev = getRelevant();
    persistGridd(); // persist current state once on mount
    return store.subscribe(() => {
      const next = getRelevant();
      if (
        next.structure !== prev.structure ||
        next.borders !== prev.borders ||
        next.cellStyles !== prev.cellStyles ||
        next.spacerStyles !== prev.spacerStyles ||
        next.localStorageKey !== prev.localStorageKey
      ) {
        prev = next;
        persistGridd();
      }
    });
  }, [store, persistGridd]);

  // Flush any pending debounced write on unmount (e.g. fast slide switch) so the
  // last edit is never dropped, and cancel the previous one when persistGridd is
  // recreated to avoid a stale closure firing.
  useEffect(() => {
    return () => {
      persistGridd.flush();
    };
  }, [persistGridd]);

  const applyPresets = () => {
    // One dispatch, one state update, one re-render cycle. The cellStyles and
    // borders slices both react to this action (borders via extraReducers).
    dispatch(applyStylesheet(currentStyleSheet));
  };

  useEffect(() => {
    // Only auto-select the first cell inside PowerPoint. Outside PPT (inline
    // embeds, read-only/standalone views) an initial selection would paint an
    // unwanted highlight — and leak into snapshots — so leave nothing selected.
    if (exists("PowerPoint")) {
      dispatch(select({ cell: { x: 0, y: 0, id: cells[0][0].id }, type: "clear" }));
    }
  }, []);

  useEffect(() => {
    applyPresets();
  }, [currentStyleSheet]);

  useEffect(() => {
    const onStorage = (e) => {
      console.warn("processing storage event", e);
      if (e.key === "DECKAI_ACTION") {
        const msg = JSON.parse(e.newValue);
        console.log("DECKAI_ACTION parsed", msg);
        dispatch(updateCell({ id: msg.target.cellId, text: msg.action.text.replace("\n", "<br />") }));
      }
    };

    window.addEventListener("storage", onStorage);

    return () => {
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const grid = React.useMemo(
    () =>
      rows?.map((row, i) =>
        cols?.map((col, j) =>
          col[0] === "s" || row[0] === "s" ? (
            <SeparatorCell
              isVertical={col[0] === "s"}
              colIndex={j}
              rowIndex={i}
              x={i}
              y={j}
              key={cells[i][j].id}
              id={cells[i][j].id}
            />
          ) : (
            <Cell cell={cells[i][j]} x={i} y={j} index={j + cols.length * i} key={cells[i][j].id} />
          )
        )
      ),
    [rows, cols, cells]
  );

  return cols?.length === 0 || rows?.length === 0 || cells?.length === 0 ? (
    <Spinner />
  ) : (
    <div
      id="container"
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        background: currentStyleSheet?.colors?.find((c) => c.name === "background")?.value,
      }}
    >
      {!readOnly && <Selection />}

      <Borders />

      <div
        id="gridd_with_headers"
        style={{
          display: "grid",
          width: "calc(100% + 1px)",
          height: "calc(100% + 1px)",
          gridTemplateColumns: "28px 1fr 28px",
          gridTemplateRows: "28px 1fr 28px",
          position: "relative",
          background: moatColor,
        }}
      >
        <div id="topleft_square" />
        <ColumnHeaders />
        <div id="topright_square" />
        <RowHeaders />

        <div
          id="gridd_content"
          ref={gridRef} // Attach ref to the grid
          className={classes.grid}
          style={{
            gridTemplateColumns: getTemplateDefs(cols),
            gridTemplateRows: getTemplateDefs(rows),
            minHeight: 0,
            minWidth: 0,
          }}
        >
          {grid}
        </div>

        <div id="midright_square" style={{ alignContent: "end", width: 28, maxWidth: 28, zIndex: 999 }}>
          {!readOnly && <StackMenu />}
        </div>

        <div id="botleft_square" />

        <div id="botmiddle_square" />

        <div id="botright_square" style={{ width: 28, zIndex: 999 }}>
          {!readOnly && <GriddMenu />}
        </div>
      </div>
    </div>
  );
};

Gridd.propTypes = {
  gridRef: PropTypes.object,
  presId: PropTypes.string,
  slideId: PropTypes.string,
};

export default Gridd;
